import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import App from "./App.tsx";
import { prefetchRoutes } from "./lib/lazyRoute";
import { getEnvironment } from "./config/environment";
import { sentryReplay } from "./lib/sentryReplay";
import {
  REPLAY_SESSION_SAMPLE_RATE,
  REPLAY_ON_ERROR_SAMPLE_RATE,
  SENTRY_IGNORE_ERRORS,
  scrubUrlForSentry,
} from "./config/sentry";
import "./index.css";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  // H-21 (security audit): PII-scrubbing helpers run on every event +
  // breadcrumb so user emails and short-lived secrets (PayPlus
  // page_request_uid, magic-link/recovery tokens) never leave the browser.
  // The Sentry Replay path already masks inputs/media; this covers the
  // structured-event + breadcrumb channels. The scrub allowlist + regex live
  // in src/config/sentry.ts (No Loose Strings) — `scrubUrlForSentry` is the
  // pure function applied to every event/breadcrumb URL below.

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: getEnvironment(),
    ignoreErrors: [...SENTRY_IGNORE_ERRORS],
    // Filter errors originating from browser extension scripts
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
    ],
    // H-21: strip email + JWT-shaped tokens from outgoing events.
    // OP-4 (validation): wrap the scrub logic in try/catch — a bug inside
    // the scrubber would otherwise drop the original event entirely (Sentry's
    // documented behavior when beforeSend throws).
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        if (event.user) {
          // Keep id (lets us correlate to PostHog), drop email + ip.
          delete event.user.email;
          delete event.user.ip_address;
          delete event.user.username;
        }
        if (event.request) {
          event.request.url = scrubUrlForSentry(event.request.url);
          // Drop full query map (already redacted in url); cookies always off.
          delete event.request.cookies;
        }
      } catch {
        // If scrubbing fails we still want the event to ship — strip
        // user/request blocks defensively so no PII leaks.
        delete event.user;
        delete event.request;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      try {
        if (breadcrumb.data && typeof breadcrumb.data === "object") {
          const data = breadcrumb.data as Record<string, unknown>;
          if (typeof data.url === "string") {
            data.url = scrubUrlForSentry(data.url);
          }
          if (typeof data.to === "string") {
            data.to = scrubUrlForSentry(data.to);
          }
          if (typeof data.from === "string") {
            data.from = scrubUrlForSentry(data.from);
          }
        }
      } catch {
        // On failure, drop breadcrumb data wholesale rather than ship raw.
        delete breadcrumb.data;
      }
      return breadcrumb;
    },
    // Session Replay — registered here but not recording until the user
    // grants analytics cookie consent (see src/lib/sentryReplay.ts).
    integrations: [sentryReplay],
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE,
  });
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

// Production-only: PostHog is never loaded on Vercel Preview or localhost so
// dev noise can't reach the dashboards. `getEnvironment()` fails safe to
// "development" when VITE_APP_ENV is unset — see src/config/environment.ts.
if (POSTHOG_KEY && getEnvironment() === "production") {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: false,
    // Heatmaps (scroll/click coordinate maps) are independent of autocapture —
    // captured on every page the SDK loads. Pairs with the project-level
    // `heatmaps_opt_in` toggle.
    capture_heatmaps: true,
    // Session replay PII protection: mask EVERY input value (emails, names,
    // payment fields, prompts) so no typed PII is ever recorded. PostHog's
    // default only masks password fields — insufficient for a paid app.
    // Mirrors the PII scrubbing already done for Sentry (src/config/sentry.ts).
    // To mask displayed (non-input) text, add the `ph-mask` class to the element.
    session_recording: {
      maskAllInputs: true,
    },
    persistence: "localStorage+cookie",
    opt_out_capturing_by_default: true,
    advanced_disable_feature_flags: true,
  });

  // Every event from this build is production by construction (init is gated
  // above). Kept so existing dashboard tiles that filter on
  // `environment = "production"` keep working until P1-9 strips both the
  // property and the filter.
  posthog.register({ environment: "production" });

  // Restore opt-in for returning users who already consented
  const stored = localStorage.getItem("flowly-cookie-consent");
  if (stored) {
    try {
      const consent = JSON.parse(stored);
      if (consent.hasConsented && consent.analytics) {
        posthog.opt_in_capturing();
      }
    } catch {
      /* invalid JSON, stay opted out */
    }
  }
}

// Embed pages (/embed/*) load inside customer-owned iframes (raw <iframe>,
// Bubble, Webflow, etc. — they all load the same URL). The Vercel rewrite
// `/(.*) → /` serves the prerendered Landing HTML for any unknown path,
// so for a fraction of a second the iframe would paint TodoAi's homepage
// before React hydrates and swaps in <BotEmbed/>. We strip that prerendered
// content here — BEFORE createRoot — and replace it with a static chat-shape
// skeleton (header / messages / input bar) so the iframe shows something
// that *looks like a chat is loading*, not a blank flash. React then mounts
// over it and BotChatPanel takes over seamlessly.
//
// Standard pattern (Intercom / Drift / Crisp all do this). Safe — static
// HTML injection before React mounts, replaced when React renders. No XSS
// surface (no user data interpolated), no CSP issue (inline style is in
// our existing allow-list), no React conflict (mount nukes the skeleton).
if (window.location.pathname.startsWith("/embed/")) {
  // The inline script in index.html hid <html> while parsing so the
  // prerendered Landing wouldn't paint. We've now reached the JS layer —
  // inject the skeleton and reveal before the browser can show anything.
  const rootEl = document.getElementById("root");
  if (rootEl) {
    // prefers-color-scheme handled inside via CSS media query so the
    // skeleton matches the iframe's actual theme without us guessing.
    rootEl.innerHTML = `
      <div class="flowly-embed-skeleton">
        <div class="flowly-embed-skeleton__header"></div>
        <div class="flowly-embed-skeleton__messages"></div>
        <div class="flowly-embed-skeleton__input">
          <div class="flowly-embed-skeleton__input-box"></div>
          <div class="flowly-embed-skeleton__send"></div>
        </div>
      </div>
      <style>
        .flowly-embed-skeleton {
          position: fixed; inset: 0;
          display: flex; flex-direction: column;
          background: #ffffff;
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
        }
        .flowly-embed-skeleton__header {
          height: 48px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        .flowly-embed-skeleton__messages { flex: 1; }
        .flowly-embed-skeleton__input {
          border-top: 1px solid #e5e7eb;
          padding: 8px;
          display: flex; gap: 8px; align-items: center;
        }
        .flowly-embed-skeleton__input-box {
          flex: 1; height: 40px;
          border: 1px solid #e5e7eb; border-radius: 8px;
          background: #f9fafb;
        }
        .flowly-embed-skeleton__send {
          width: 40px; height: 40px;
          border-radius: 8px;
          background: #e5e7eb;
        }
        @media (prefers-color-scheme: dark) {
          .flowly-embed-skeleton { background: #0a0a0a; }
          .flowly-embed-skeleton__header { background: #171717; border-color: #262626; }
          .flowly-embed-skeleton__input { border-color: #262626; }
          .flowly-embed-skeleton__input-box { background: #171717; border-color: #262626; }
          .flowly-embed-skeleton__send { background: #262626; }
        }
      </style>`;
  }
  // Skeleton is in the DOM. Safe to reveal — the browser will paint the
  // skeleton directly, never the Landing underneath.
  document.documentElement.style.visibility = "visible";
}

createRoot(document.getElementById("root")!).render(
  <PostHogProvider client={posthog}>
    <App />
  </PostHogProvider>
);

// Warm every route chunk once the browser is idle, so in-app navigation never
// waits on a lazy import (no <Suspense> fallback flash). See src/lib/lazyRoute.ts.
prefetchRoutes();
